import { supabase } from './supabase.js';

const BROADCAST_EVENT = 'msg';

// IMPORTANT: the channel is partitioned PER BUSINESS so KDS / customer-display
// broadcasts never cross tenants. The channel names embed the business id, so a
// business only ever receives its own orders/carts. Nothing is broadcast until
// init(businessId) is called - the main app calls it in initApp, and each popup
// (KDS / CFD) calls it at startup with pointbunny_business_id from localStorage.
class PointbunnyChannel {
  constructor() {
    this._handler = null;
    this._bc = null;
    this._ch = null;
    this._businessId = null;
    this._readyResolve = null;
    // Resolves once the channel is bound to a business and SUBSCRIBED.
    this.ready = new Promise((resolve) => { this._readyResolve = resolve; });

    // Private channels authorize against the user's JWT, so keep Realtime's token
    // in sync when the session refreshes - otherwise the join silently drops later.
    supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.access_token) supabase.realtime.setAuth(session.access_token);
    });
  }

  // Bind the broadcast channel to one business. Idempotent for the same id;
  // rebinds (and tears down the old channel) on a business switch in the same tab.
  init(businessId) {
    if (!businessId || businessId === this._businessId) return;
    this._teardown();
    this._businessId = businessId;
    this._bind(businessId);
  }

  async _bind(businessId) {
    // PRIVATE channel: Realtime must be authenticated with the user's access token
    // BEFORE subscribing, or the authorized join is denied (this is what broke sync
    // last time - the popups never set it). Works in every window because they all
    // share the same Supabase session from localStorage.
    const { data: { session } } = await supabase.auth.getSession();
    // AWAIT setAuth so the token is applied to the socket BEFORE we subscribe -
    // otherwise the private-channel join races ahead with no auth and is denied.
    if (session?.access_token) await supabase.realtime.setAuth(session.access_token);
    if (businessId !== this._businessId) return; // a newer init() superseded this one

    // Native BroadcastChannel: synchronous, zero-network, same-browser windows only.
    // Used alongside Supabase Realtime so messages survive brief connection drops.
    if (typeof BroadcastChannel !== 'undefined') {
      this._bc = new BroadcastChannel(`pointbunny-local-${businessId}`);
      this._bc.onmessage = ({ data }) => { if (this._handler) this._handler({ data }); };
    }

    // private:true enforces Realtime Authorization - a client can only join this
    // topic if the realtime.messages RLS policy says they belong to this business.
    // Even knowing the business id isn't enough to eavesdrop; you must be a member.
    this._ch = supabase.channel(`pointbunny-displays-${businessId}`, {
      config: { private: true, broadcast: { self: false } },
    });
    this._ch.on('broadcast', { event: BROADCAST_EVENT }, ({ payload }) => {
      if (this._handler) this._handler({ data: payload });
    });
    this._ch.subscribe((status, err) => {
      if (status === 'SUBSCRIBED') {
        console.info('[pointbunny] display channel SUBSCRIBED:', `pointbunny-displays-${businessId}`);
        if (this._readyResolve) { this._readyResolve(); this._readyResolve = null; }
      } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
        // CHANNEL_ERROR here almost always = the private-channel authorization was
        // denied (no/old auth token, or the realtime.messages RLS policy didn't match).
        console.error('[pointbunny] display channel join failed:', status, err ?? '');
      }
    });
  }

  _teardown() {
    if (this._ch) { supabase.removeChannel(this._ch); this._ch = null; }
    if (this._bc) { this._bc.close(); this._bc = null; }
  }

  postMessage(data) {
    if (!this._ch) return; // not bound to a business yet - drop rather than leak globally
    this._ch.send({ type: 'broadcast', event: BROADCAST_EVENT, payload: data });
    this._bc?.postMessage(data);
  }

  set onmessage(fn) {
    this._handler = fn;
  }
}

const channel = new PointbunnyChannel();
export default channel;

// ── Message types ─────────────────────────────────────────────────────────────

export const MSG = {
  // KDS: cashier → kitchen
  KDS_QUEUE_SYNC:   'KDS_QUEUE_SYNC',   // full queue snapshot
  // KDS: kitchen → cashier
  KDS_REQUEST_SYNC: 'KDS_REQUEST_SYNC', // kitchen window just opened, needs current queue
  KDS_ORDER_DONE:   'KDS_ORDER_DONE',   // cook committed an order as done (after undo window)
  KDS_ORDER_READY:  'KDS_ORDER_READY',  // order marked Ready for Pickup; stays in the queue
  KDS_ORDER_VOIDED: 'KDS_ORDER_VOIDED', // cashier/manager voided a transaction; remove from KDS
  // Terminal → terminals: a sale was recorded, so revenue views (today's cards,
  // an open Cashflow panel) refetch. { id }. Displays ignore it.
  SALE_RECORDED:    'SALE_RECORDED',

  // CFD: cashier → customer display
  CFD_CART_UPDATE:    'CFD_CART_UPDATE',    // cart changed (add/remove/clear)
  CFD_SALE_COMPLETE:  'CFD_SALE_COMPLETE',  // transaction finished
  CFD_SHOW_QR:        'CFD_SHOW_QR',        // show/hide the merchant QR overlay (QR payment selected)
  CFD_TIP_REQUEST:    'CFD_TIP_REQUEST',    // show (show:true) or close the "Add a tip?" screen
  // CFD: customer display → cashier
  CFD_REQUEST_SYNC:   'CFD_REQUEST_SYNC',   // CFD just opened, needs current cart
  CFD_TIP_SELECTED:   'CFD_TIP_SELECTED',   // customer picked a tip (echoes the request id)

  // Time clock → POS: somebody clocked in/out or started/ended a break, so the
  // home "on shift now" bubble refreshes immediately instead of on the next poll.
  // NOTE: timeclock.js hardcodes this string (the kiosk stays standalone and
  // doesn't import this module) - keep the two in sync.
  SHIFT_SYNC: 'SHIFT_SYNC',

  // Inventory: a terminal changed stock (sale, void, restock, link edit).
  // No payload - receivers refetch (stock is tiny; refetching avoids drift).
  INVENTORY_SYNC: 'INVENTORY_SYNC',

  // Tables: a terminal seated, ordered, paid or edited tables. No payload -
  // receivers refetch the floor (tables, open tabs, bookings, waitlist).
  TABLES_SYNC: 'TABLES_SYNC',
};
